import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Who wears the gold leader's crown (Jack, 2026-10-29): the #1 player of each
// leaderboard (Jangle, Raider Races), unless they turned it off in Settings.
// One shared copy for the whole app, fetched again after a minute so the
// crown moves when leadership changes.
const FRESH_MS = 60000;
let cache = { crowns: {}, at: 0, loading: null };
const listeners = new Set();

export function refreshCrowns() {
  if (cache.loading) return cache.loading;
  cache.loading = api.get("/players/crowns")
    .then(({ data }) => { cache = { crowns: data?.crowns || {}, at: Date.now(), loading: null }; })
    .catch(() => { cache = { ...cache, at: Date.now(), loading: null }; })
    .finally(() => listeners.forEach((fn) => fn(cache.crowns)));
  return cache.loading;
}

export const CROWN_TITLES = { jangle: "Jangle", race: "Raider Races", live: "Live Games" };

// `category` is one category or a list: a Live Game shows the crown of the
// Jangle leader and of the Live Games leader.
export function leadsAny(leads, category) {
  const cats = Array.isArray(category) ? category : [category];
  return cats.find((c) => c && leads.includes(c)) || null;
}

// The categories `userId` leads, e.g. ["jangle"], or [] for no crown.
export function useCrown(userId) {
  const { user } = useAuth();
  const [crowns, setCrowns] = useState(cache.crowns);
  useEffect(() => {
    listeners.add(setCrowns);
    if (Date.now() - cache.at > FRESH_MS) refreshCrowns();
    return () => listeners.delete(setCrowns);
  }, []);
  // Your own crown hides the moment you switch it off, before the next fetch.
  if (!userId || (user?.id === userId && user?.settings?.crown === "off")) return [];
  return crowns[userId] || [];
}

// Whether `userId` wears the crown in a `category` game, for the game board's
// raiders (Jack: only in the kind of game they lead). `me` is the signed-in
// user, whose own switch in Settings counts straight away.
export function fetchCrowns() {
  return Date.now() - cache.at > FRESH_MS ? refreshCrowns().then(() => cache.crowns) : Promise.resolve(cache.crowns);
}
export function wearsCrown(crowns, userId, category, me) {
  if (!userId || !category || (me?.id === userId && me?.settings?.crown === "off")) return false;
  return !!leadsAny(crowns?.[userId] || [], category);
}
